/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import {
	InspectorControls,
	RichText,
	useBlockProps,
	__experimentalUseBorderProps as useBorderProps,
	__experimentalUseColorProps as useColorProps,
	__experimentalGetSpacingClassesAndStyles as useSpacingProps,
} from '@wordpress/block-editor';
import Settings from './settings';

import { useRef, useEffect } from '@wordpress/element';
import { useInstanceId } from '@wordpress/compose';

function InputFieldBlock( props ) {
	const { attributes, setAttributes, className } = props;
	const { type, name, label, hidden, required, placeholder, value, id } =
		attributes;
	const blockProps = useBlockProps( {
		className: clsx( className, {
			'is-label-inline': 'checkbox' === type,
			'missing-name': ! name,
		} ),
	} );
	const instanceId = useInstanceId( InputFieldBlock );
	const ref = useRef();
	const TagName = type === 'textarea' ? 'textarea' : 'input';

	const borderProps = useBorderProps( attributes );
	const colorProps = useColorProps( attributes );
	const spacingProps = useSpacingProps( attributes );
	if ( ref.current ) {
		ref.current.focus();
	}

	useEffect( () => {
		if ( ! id ) {
			setAttributes( { id: 'popper-input-' + instanceId } );
		}
	}, [] );

	if ( 'hidden' === type ) {
		return (
			<>
				{ controls }
				<input
					type="hidden"
					aria-label={ __( 'Value' ) }
					value={ value }
					onChange={ ( event ) =>
						setAttributes( { value: event.target.value } )
					}
					__nextHasNoMarginBottom
				/>
			</>
		);
	}

	return (
		<>
			<Settings { ...props } />
			<div { ...blockProps }>
				<div hidden={ hidden } className="popper-form-label" htmlFor={ 'field-' + instanceId }>
					<RichText
						tagName="span"
						value={ label }
						onChange={ ( newLabel ) =>
							setAttributes( { label: newLabel } )
						}
						aria-label={
							label ? __( 'Label' ) : __( 'Empty label' )
						}
						data-empty={ label ? false : true }
						placeholder={ __( 'Type the label for this input' ) }
					/>
					{ required && <span className="required">*</span> }
				</div>
				<TagName
					id={ 'field-' + instanceId }
					type={ 'textarea' === type ? undefined : type }
					className={ clsx(
						className,
						colorProps.className,
						borderProps.className,
						spacingProps.className
					) }
					aria-label={ __( 'Optional placeholder text' ) }
					// We hide the placeholder field's placeholder when there is a value. This
					// stops screen readers from reading the placeholder field's placeholder
					// which is confusing.
					placeholder={
						placeholder ? undefined : __( 'Optional placeholder…' )
					}
					value={ placeholder }
					onChange={ ( event ) =>
						setAttributes( { placeholder: event.target.value } )
					}
					aria-required={ required }
					style={ {
						...borderProps.style,
						...colorProps.style,
						...spacingProps.style,
					} }
				/>
			</div>
		</>
	);
}

export default InputFieldBlock;
